import { useState } from "react";
import * as React from "react";
import {
  Plus,
  LineChart,
  History,
  LogOut,
  ShoppingCart,
  Settings,
  Menu,
} from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ThemeToggle } from "../../components/ui/theme_toggle";
import { ScrollArea } from "../../components/ui/scroll_area";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import { cn } from "../../lib/utils";
import img_text from "./forCreators_text.png";
import usePayment from "../../hooks/payment_hook";

const SIDEBAR_WIDTH = 280;

type NavItem = {
  title: string;
  to: string;
  icon: React.ReactNode;
};

function NavLink({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: string;
  onClick?: () => void;
}) {
  const isActive = active === item.to;
  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
        isActive
          ? "bg-primary text-primary-foreground"
          : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
      )}
    >
      <span className="shrink-0">{item.icon}</span>
      <span>{item.title}</span>
    </Link>
  );
}

function SidebarContent({
  user,
  active,
  setActive,
  onNavClick,
}: {
  user: any;
  active: string;
  setActive: (path: string) => void;
  onNavClick?: () => void;
}) {
  const { hasPaid, isCheckingPayment } = usePayment();

  const navItems: NavItem[] = [
    { title: "Add Test", to: "/add", icon: <Plus className="h-4 w-4" /> },
    { title: "Current Tests", to: "/tests", icon: <LineChart className="h-4 w-4" /> },
    { title: "Finished Tests", to: "/finished", icon: <History className="h-4 w-4" /> },
  ];

  const bottomItems: NavItem[] = [
    ...(!hasPaid
      ? [{ title: "Subscribe Now", to: "/checkout", icon: <ShoppingCart className="h-4 w-4" /> }]
      : []),
    { title: "Settings", to: "/settings", icon: <Settings className="h-4 w-4" /> },
    { title: "Log Out", to: "/logout", icon: <LogOut className="h-4 w-4" /> },
  ];

  const handleClick = (to: string) => {
    setActive(to);
    onNavClick?.();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center px-4 py-4">
        <img src={img_text} className="h-8 w-auto object-contain" alt="forCreators" />
      </div>

      <div className="h-px bg-border mx-4" />

      <ScrollArea className="flex-1 px-3 py-3">
        <nav className="flex flex-col gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              item={item}
              active={active}
              onClick={() => handleClick(item.to)}
            />
          ))}
        </nav>
      </ScrollArea>

      <div className="px-3 pb-3 flex flex-col gap-1">
        {bottomItems.map((item) => (
          <NavLink
            key={item.to}
            item={item}
            active={active}
            onClick={() => handleClick(item.to)}
          />
        ))}
      </div>

      <div className="h-px bg-border mx-4" />

      <div className="flex items-center gap-3 px-4 py-3">
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name ?? "User"}
            className="h-9 w-9 rounded-full shrink-0 object-cover"
          />
        ) : (
          <div className="h-9 w-9 rounded-full bg-muted shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          {!isCheckingPayment && !hasPaid && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <ShoppingCart className="h-3 w-3" />
              Not a Subscriber
            </span>
          )}
        </div>
        <ThemeToggle />
      </div>
    </div>
  );
}

export default function Sidebar({ user }: any) {
  const location = useLocation();
  const [active, setActive] = useState<string>(location.pathname);
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      <aside
        className="hidden lg:flex flex-col bg-background border-r border-border shadow-sidebar"
        style={{ width: SIDEBAR_WIDTH }}
      >
        <SidebarContent user={user} active={active} setActive={setActive} />
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 bg-background border-b border-border">
        <img src={img_text} className="h-7 w-auto object-contain" alt="forCreators" />
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px]">
            <SidebarContent
              user={user}
              active={active}
              setActive={setActive}
              onNavClick={() => setSheetOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>

      <div className="lg:hidden h-[57px] w-full shrink-0" />
    </>
  );
}
